import type { MutableRefObject } from 'react'
import type { Dispatch } from 'redux'
import type { PostHog } from 'posthog-js'
import { getPersonalityDisplayName, PERSONALITY_SAGE_ID } from '../../../services/oracle/personalities'
import type { VideoPlayerPlaybackState } from '../../OracleCommons/useVideoState'
import type { PostHogEventContext } from '../PostHogProvider'

/**
 * Shared context payload threaded through every event-callback factory.
 *
 * `usePostHogEvent` keeps the live values in a ref so each callback reads
 * fresh data without needing to be recreated on every render — that's why
 * factories receive a ref rather than the values themselves.
 *
 * `posthogClient` is statically `PostHog` from `usePostHog()`, but tests
 * (testdouble) replace the hook to return `null`, so callbacks must still
 * guard with `if (!ctx.posthogClient) return` before calling `.capture()`.
 */
export interface PostHogCallbackContext {
  posthogClient: PostHog | null
  eventContext: PostHogEventContext | null
  // `playingContentId` / `playingFeatureMediaId` are the in-video-guide Layer 3
  // pair. Renamed from `contentId` / `featureMediaId` to free up the
  // `content_id` payload name for the recommendation events, which describe
  // a *recommended* item rather than the currently-playing one.
  playingContentId: string | null
  playingFeatureMediaId: string | null
  getPlaybackState: () => VideoPlayerPlaybackState
  chatSessionId: string | null
  conversationTitle: string | null
  // `aiPersona` is the persona key (e.g. `sage`, `architect`) active in this
  // conversation. Layer 3 (per-conversation), not Layer 1 (per-user), because
  // the persona is locked at chat entry and never changes mid-session — see
  // [!q-persona-layer-assignment] in docs/analytics/questions.md.
  aiPersona: string | null
  // `chatExperienceName` is the backend chat channel for this conversation
  // (`Default`, `Tarot`, `Horoscope`, …). Sourced from the GraphQL
  // `chatChannel` field; distinct from `eventContext` (surface-driven).
  chatExperienceName: string | null
  // Redux dispatch so a fan-out callback can fire its GA4 leg through the
  // existing event-tracking pipeline (middleware enriches ai_persona,
  // app_name, gaia_uuid, …) alongside the direct PostHog `.capture()` leg.
  dispatch: Dispatch
}

export type PostHogContextRef = MutableRefObject<PostHogCallbackContext>

/**
 * Builds the Layer 2 (surface) + Layer 3 (in-chat + in-video-guide) auto-attach
 * block for any event fired inside an AI chat conversation.
 *
 * Each property is conditionally spread so a missing source value never
 * produces a `null`/`undefined` payload property — analytics queries that
 * filter on presence would otherwise treat absent and `null` differently.
 *
 * Used by every chat-domain event factory (`createChatEventCallbacks`,
 * `createModerationEventCallbacks`, …) so the auto-attach block has a single
 * point of definition. Adding a new Layer 3 field is a one-line change here
 * that propagates to every consumer.
 *
 * In-video-guide pair (`playing_content_id` / `playing_feature_media_id`) is
 * spread from the same helper because events fired inside an AI chat that is
 * itself rendered inside the Video Guide need both contexts on the payload.
 */
export const buildInChatContextProperties = (
  ctx: PostHogCallbackContext,
): Record<string, string | number | null> => ({
  ...(ctx.eventContext && { context: ctx.eventContext }),
  ...(ctx.chatSessionId && {
    chat_session_id: ctx.chatSessionId,
    // Emit explicit null when in a chat session whose title hasn't arrived
    // from Apollo cache yet, so analyst queries can distinguish "never had
    // a chat session" (property absent) from "in a chat session, title not
    // yet generated" (property present with null value). Spec: docs/analytics/design.md:103,452.
    conversation_title: ctx.conversationTitle ?? null,
  }),
  // `ai_persona` is NOT conditionally omitted — it mirrors the GA4
  // middleware's `… || SAGE` default ("Sage unless the user explicitly
  // selected another persona in the main chat"). It is always emitted,
  // falling back to Sage when no explicit persona is in context.
  //
  // The value is WRAPPED through `getPersonalityDisplayName` (not merely
  // defaulted when null): a raw persona key/id in context (`'sage'`, `2`)
  // is normalized to its canonical display name (`'Gaia Sage'`), exactly
  // like the GA4 middleware chokepoint. This guarantees PostHog's emitted
  // `ai_persona` vocabulary always matches GA4's normative display names.
  // The helper is idempotent on display names and passes truly-unknown
  // strings through unchanged.
  ai_persona: getPersonalityDisplayName(ctx.aiPersona || PERSONALITY_SAGE_ID),
  ...(ctx.chatExperienceName && { chat_experience_name: ctx.chatExperienceName }),
  ...(ctx.playingContentId != null && { playing_content_id: ctx.playingContentId }),
  ...(ctx.playingFeatureMediaId != null && { playing_feature_media_id: ctx.playingFeatureMediaId }),
  ...(() => {
    const t = ctx.getPlaybackState?.().currentTimeSeconds
    return t != null ? { video_time_seconds: t } : {}
  })(),
})
